const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]));
const draftKey = root => {
    const user = Number(window.khThemeRuntime?.userId || 0);
    return user > 0 ? `teejit.self-check.${user}.${root.dataset.contentId}.${root.dataset.assessmentId}` : null;
};
export const clearSelfCheckDraft = root => {
    try { const key = draftKey(root); if (key) window.sessionStorage.removeItem(key); } catch { /* storage is optional */ }
};
export const selfCheckState = root => Array.from(root.querySelectorAll('[data-self-check-panel]')).map(panel => ({
    section: panel.dataset.selfCheckPanel,
    notes: panel.querySelector('textarea').value,
    priority: panel.querySelector('select').value || null,
}));

export const initSelfCheck = root => {
    if (!root || root.dataset.assessmentVariant !== 'self_check' || root.dataset.selfCheckReady) return;
    const form = root.querySelector('[data-test-questions-form]');
    if (!form) return;
    root.dataset.selfCheckReady = '1';
    const copy = JSON.parse(root.dataset.selfCheckCopy || '{}');
    const fields = Array.from(form.querySelectorAll('fieldset[data-question-id]'));
    const groups = new Map();
    fields.forEach(field => {
        const key = field.dataset.selfCheckSection || '';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(field);
    });
    const progress = document.createElement('p');
    progress.setAttribute('role', 'status');
    progress.className = 'self-check-progress';
    const nav = document.createElement('div');
    nav.className = 'self-check-navigation';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', copy.sections);
    const panels = [];
    const buttons = [];
    form.prepend(progress, nav);
    const show = index => panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        buttons[i].setAttribute('aria-pressed', i === index ? 'true' : 'false');
    });
    groups.forEach((questions, title) => {
        const panel = document.createElement('section');
        panel.className = 'self-check-section';
        panel.dataset.selfCheckPanel = title;
        const heading = document.createElement('h4');
        heading.textContent = title;
        panel.append(heading, ...questions);
        const notes = document.createElement('label');
        notes.textContent = copy.notes;
        const textarea = document.createElement('textarea');
        textarea.maxLength = 10000;
        notes.append(textarea);
        const priority = document.createElement('label');
        priority.textContent = copy.priority;
        const select = document.createElement('select');
        ['', 'high', 'medium', 'low'].forEach(value => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value ? copy[value] : '–';
            select.append(option);
        });
        priority.append(select);
        panel.append(notes, priority);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-outline-primary';
        button.textContent = title;
        const index = panels.length;
        button.addEventListener('click', () => show(index));
        buttons.push(button); panels.push(panel); nav.append(button);
        nav.after(panel);
    });
    // Keep panels in question order after moving the existing controls.
    let anchor = nav;
    panels.forEach(panel => { anchor.after(panel); anchor = panel; });
    if (root.dataset.assessmentLocked !== '1') {
        try {
            const key = draftKey(root);
            const draft = key ? JSON.parse(window.sessionStorage.getItem(key) || 'null') : null;
            if (draft && Date.now() - draft.saved < 7 * 24 * 60 * 60 * 1000) {
                fields.forEach(field => field.querySelectorAll('input').forEach(input => {
                    input.checked = String(draft.answers?.[field.dataset.questionId] || '') === input.value;
                }));
                panels.forEach(panel => {
                    const entry = draft.state?.find(entry => entry.section === panel.dataset.selfCheckPanel);
                    if (entry) {
                        panel.querySelector('textarea').value = String(entry.notes || '');
                        panel.querySelector('select').value = String(entry.priority || '');
                    }
                });
            }
        } catch { /* missing or unavailable browser storage */ }
    }
    const persist = () => {
        if (root.dataset.assessmentLocked === '1') return;
        try {
            const key = draftKey(root);
            if (key) window.sessionStorage.setItem(key, JSON.stringify({saved: Date.now(), state: selfCheckState(root),
                answers: Object.fromEntries(fields.map(field => [field.dataset.questionId, field.querySelector('input:checked')?.value || '']))}));
        } catch { /* the form remains usable without storage */ }
    };
    const update = () => {
        const answered = fields.filter(field => field.querySelector('input:checked')).length;
        progress.textContent = `${answered} / ${fields.length} · ${copy.answered}`;
    };
    form.addEventListener('change', update);
    form.addEventListener('change', persist);
    form.addEventListener('input', persist);
    update(); show(0);
};

export const renderSelfCheckResult = (result, copy, canRestart, repeatTest) => {
    const summary = result.self_check;
    const percent = summary.percent == null ? '–' : `${summary.percent}%`;
    return `<div class="self-check-result">
        <h4>${escape(summary.band?.label || copy.result)}</h4>
        <p class="self-check-score">${escape(percent)} · ${escape(summary.score)} / ${escape(summary.max_score)} ${escape(copy.points)}</p>
        <p>${escape(summary.band?.text)}</p>
        ${summary.answered < summary.total ? `<p class="t-notice">${escape(copy.incomplete)}</p>` : ''}
        ${summary.percent == null ? `<p>${escape(copy.noApplicable)}</p>` : ''}
        <div class="self-check-table-wrap"><table><thead><tr><th>${escape(copy.sections)}</th><th>${escape(copy.points)}</th><th>%</th><th>${escape(copy.priority)}</th></tr></thead><tbody>
        ${(summary.sections || []).map(section => `<tr><td>${escape(section.title)}</td><td>${escape(section.score)} / ${escape(section.max_score)}</td><td>${section.percent == null ? '–' : escape(section.percent) + '%'}</td><td>${escape(copy[section.priority] || '–')}</td></tr>
            ${section.notes ? `<tr><td colspan="4">${escape(copy.notes)}: ${escape(section.notes)}</td></tr>` : ''}`).join('')}
        </tbody></table></div>
        <button type="button" class="btn btn-outline-primary" data-self-check-print>${escape(copy.print)}</button>
        ${canRestart ? `<button type="button" class="btn btn-primary" data-test-restart>${escape(repeatTest)}</button>` : ''}
    </div>`;
};
